import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { BuilderComponentType } from '../interfaces/builder-screen.models';

export interface PanelPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  panels: {
    [panelId: string]: boolean; // true = expandido, false = colapsado
  };
}

export interface ComponentPanelState {
  componentType: BuilderComponentType;
  panelStates: {
    [panelId: string]: boolean;
  };
  lastUsed: Date;
  usageCount: number;
}

export interface SmartSuggestion {
  panelId: string;
  reason: string;
  confidence: number; // 0-1
  action: 'expand' | 'collapse';
}

@Injectable({
  providedIn: 'root'
})
export class SmartPanelManagerService {
  private readonly STORAGE_KEY = 'builder-screen-panel-states';
  private readonly PRESETS_KEY = 'builder-screen-panel-presets';
  
  private panelStatesSubject = new BehaviorSubject<Map<BuilderComponentType, ComponentPanelState>>(new Map());
  private currentPresetSubject = new BehaviorSubject<string | null>(null);
  
  panelStates$ = this.panelStatesSubject.asObservable();
  currentPreset$ = this.currentPresetSubject.asObservable();

  constructor() {
    this.loadPanelStates();
  }

  getCurrentPreset(): string | null {
    return this.currentPresetSubject.value;
  }

  setCurrentPreset(presetId: string): void {
    this.currentPresetSubject.next(presetId);
  }

  // Presets de painel padrão
  getDefaultPresets(): PanelPreset[] {
    return [
      {
        id: 'minimal',
        name: 'builderScreen.presets.minimal.name',
        description: 'builderScreen.presets.minimal.description',
        icon: 'minus-circle',
        panels: {
          essential: true,
          appearance: false,
          layout: false,
          database: false,
          validation: false,
          advanced: false
        }
      },
      {
        id: 'standard',
        name: 'builderScreen.presets.standard.name',
        description: 'builderScreen.presets.standard.description',
        icon: 'setting',
        panels: {
          essential: true,
          appearance: true,
          layout: true,
          database: true,
          validation: false,
          advanced: false
        }
      },
      {
        id: 'advanced',
        name: 'builderScreen.presets.advanced.name',
        description: 'builderScreen.presets.advanced.description',
        icon: 'experiment',
        panels: {
          essential: true,
          appearance: true,
          layout: true,
          database: true,
          validation: true,
          advanced: true
        }
      },
      {
        id: 'developer',
        name: 'builderScreen.presets.developer.name',
        description: 'builderScreen.presets.developer.description',
        icon: 'code',
        panels: {
          essential: false,
          appearance: false,
          layout: true,
          database: true,
          validation: true,
          advanced: true
        }
      }
    ];
  }

  // Obtém estado do painel para tipo específico de componente
  getPanelState(componentType: BuilderComponentType, panelId: string): boolean {
    const states = this.panelStatesSubject.value;
    const componentState = states.get(componentType);
    
    if (!componentState) {
      return this.getDefaultPanelState(componentType, panelId);
    }
    
    return componentState.panelStates[panelId] ?? this.getDefaultPanelState(componentType, panelId);
  }

  // Atualiza estado do painel para tipo específico de componente
  updatePanelState(componentType: BuilderComponentType, panelId: string, isExpanded: boolean): void {
    const states = this.panelStatesSubject.value;
    let componentState = states.get(componentType);
    
    if (!componentState) {
      componentState = {
        componentType,
        panelStates: {},
        lastUsed: new Date(),
        usageCount: 0
      };
    }
    
    componentState.panelStates[panelId] = isExpanded;
    componentState.lastUsed = new Date();
    componentState.usageCount++;
    
    states.set(componentType, componentState);
    this.panelStatesSubject.next(new Map(states));
    this.savePanelStates();
  }

  // Aplica preset ao tipo de componente
  applyPreset(componentType: BuilderComponentType, presetId: string): void {
    const presets = this.getDefaultPresets();
    const preset = presets.find(p => p.id === presetId);
    
    if (!preset) return;
    
    const states = this.panelStatesSubject.value;
    const componentState: ComponentPanelState = {
      componentType,
      panelStates: { ...preset.panels },
      lastUsed: new Date(),
      usageCount: 1
    };
    
    states.set(componentType, componentState);
    this.panelStatesSubject.next(new Map(states));
    this.currentPresetSubject.next(presetId);
    this.savePanelStates();
  }

  // Obtém sugestões inteligentes para componente
  getSmartSuggestions(componentType: BuilderComponentType): SmartSuggestion[] {
    const suggestions: SmartSuggestion[] = [];
    const states = this.panelStatesSubject.value;
    const componentState = states.get(componentType);
    
    // Sugere baseado no tipo de componente
    switch (componentType) {
      case 'text':
      case 'textarea':
        suggestions.push({
          panelId: 'validation',
          reason: 'builderScreen.suggestions.textValidation',
          confidence: 0.8,
          action: 'expand'
        });
        break;
        
      case 'button':
        suggestions.push({
          panelId: 'appearance',
          reason: 'builderScreen.suggestions.buttonStyling',
          confidence: 0.9,
          action: 'expand'
        });
        break;
        
      case 'table':
      case 'simpletable':
        suggestions.push({
          panelId: 'advanced',
          reason: 'builderScreen.suggestions.tableFeatures',
          confidence: 0.7,
          action: 'expand'
        });
        break;
    }
    
    // Sugere baseado nos padrões de uso
    if (componentState && componentState.usageCount > 5) {
      const mostUsedPanels = Object.entries(componentState.panelStates)
        .filter(([_, isExpanded]) => isExpanded)
        .map(([panelId]) => panelId);
        
      if (mostUsedPanels.length > 0) {
        suggestions.push({
          panelId: mostUsedPanels[0],
          reason: 'builderScreen.suggestions.frequentlyUsed',
          confidence: 0.6,
          action: 'expand'
        });
      }
    }
    
    return suggestions.sort((a, b) => b.confidence - a.confidence);
  }

  // Auto-colapsa painéis não utilizados baseado no tipo de componente
  autoCollapseUnusedPanels(componentType: BuilderComponentType): void {
    const relevantPanels = this.getRelevantPanels(componentType);
    const states = this.panelStatesSubject.value;
    let componentState = states.get(componentType);
    
    if (!componentState) {
      componentState = {
        componentType,
        panelStates: {},
        lastUsed: new Date(),
        usageCount: 0
      };
    }
    
    // Colapsa painéis irrelevantes
    const allPanels = ['essential', 'appearance', 'layout', 'database', 'validation', 'advanced'];
    allPanels.forEach(panelId => {
      if (!relevantPanels.includes(panelId)) {
        componentState!.panelStates[panelId] = false;
      }
    });
    
    states.set(componentType, componentState);
    this.panelStatesSubject.next(new Map(states));
    this.savePanelStates();
  }

  // Obtém painéis relevantes para tipo de componente
  private getRelevantPanels(componentType: BuilderComponentType): string[] {
    const basePanels = ['essential', 'layout'];
    
    switch (componentType) {
      case 'text':
      case 'textarea':
      case 'number':
        return [...basePanels, 'appearance', 'database', 'validation'];
        
      case 'button':
        return [...basePanels, 'appearance', 'advanced'];
        
      case 'switch':
        return [...basePanels, 'appearance', 'database'];
        
      case 'table':
      case 'simpletable':
        return [...basePanels, 'appearance', 'advanced'];
        
      case 'date':
      case 'select':
      case 'querySelect':
        return [...basePanels, 'appearance', 'database', 'validation'];
        
      default:
        return basePanels;
    }
  }

  // Obtém estado padrão do painel para tipo de componente e painel
  private getDefaultPanelState(componentType: BuilderComponentType, panelId: string): boolean {
    // Painel essencial sempre expandido por padrão
    if (panelId === 'essential') return true;
    
    // Padrões específicos do componente
    const relevantPanels = this.getRelevantPanels(componentType);
    
    // Expande painéis relevantes, colapsa outros
    return relevantPanels.includes(panelId);
  }

  // Salva estados dos painéis no localStorage
  private savePanelStates(): void {
    try {
      const states = this.panelStatesSubject.value;
      const serializable = Array.from(states.entries()).map(([key, value]) => [
        key,
        {
          ...value,
          lastUsed: value.lastUsed.toISOString()
        }
      ]);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(serializable));
    } catch (error) {
      console.warn('Failed to save panel states:', error);
    }
  }

  // Carrega estados dos painéis do localStorage
  private loadPanelStates(): void {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        const states = new Map<BuilderComponentType, ComponentPanelState>();
        
        parsed.forEach(([key, value]: [BuilderComponentType, any]) => {
          states.set(key, {
            ...value,
            lastUsed: new Date(value.lastUsed)
          });
        });
        
        this.panelStatesSubject.next(states);
      }
    } catch (error) {
      console.warn('Failed to load panel states:', error);
    }
  }

  // Reseta todos os estados dos painéis
  resetPanelStates(): void {
    this.panelStatesSubject.next(new Map());
    this.currentPresetSubject.next(null);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Obtém estatísticas de uso
  getUsageStatistics(): { [componentType: string]: { usageCount: number; lastUsed: Date } } {
    const states = this.panelStatesSubject.value;
    const stats: { [componentType: string]: { usageCount: number; lastUsed: Date } } = {};
    
    states.forEach((state, componentType) => {
      stats[componentType] = {
        usageCount: state.usageCount,
        lastUsed: state.lastUsed
      };
    });
    
    return stats;
  }
}
